/**
 * `<tours-panel>`: the list of guided tours, opened from "Show me how" in the
 * header and "Guided tours" in the account menu. Each tour shows how far this
 * person got, and starts it (again) from the first step.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { serverFlags } from '../../state/feature-flags.ts';
import { pageVisibility } from '../../state/page-visibility.ts';
import { lockedBy, startTour, toursPanelOpen } from './tour-engine.ts';
import { loadTourProgress, tourProgress } from './tour-progress.ts';
import { availableTours, type Tour } from './tour-registry.ts';

@customElement('tours-panel')
export class ToursPanel extends LightElement {
  // Features and Pages decide which tours show. The account's features can
  // arrive after the list is open, on a slow start.
  private store = new StoreController(this, [toursPanelOpen, tourProgress, serverFlags, pageVisibility]);

  override connectedCallback(): void {
    super.connectedCallback();
    // Mounted once in the app, so this is the boot read of the account's copy.
    void loadTourProgress();
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('pointerdown', this.onOutside, true);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('pointerdown', this.onOutside, true);
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && toursPanelOpen.value) toursPanelOpen.set(false);
  };

  /** A click outside closes it, except on the buttons that open it. */
  private onOutside = (e: PointerEvent): void => {
    if (!toursPanelOpen.value || !(e.target instanceof Element)) return;
    if (this.contains(e.target) || e.target.closest('[data-opens-tours]')) return;
    toursPanelOpen.set(false);
  };

  private renderTour(t: Tour): TemplateResult {
    const p = tourProgress.value[t.id];
    const first = lockedBy(t);
    if (first) {
      return html`<div class="tours-row is-after">
        <div class="tours-row-head"><strong>${t.title}</strong><span class="tour-pill is-after">Locked</span></div>
        <p>${t.description} · ${t.minutes} min</p>
        <p class="tours-row-lock">Finish <strong>${first.title}</strong> first. This tour uses what you make there.</p>
        <div class="tours-row-acts">
          <button class="btn btn-secondary" @click=${() => startTour(first.id)}>Start ${first.title}</button>
        </div>
      </div>`;
    }
    const pill = !p
      ? html`<span class="tour-pill is-new">New</span>`
      : p.state === 'finished'
        ? html`<span class="tour-pill is-done">Finished</span>`
        : html`<span class="tour-pill is-stopped">Stopped at ${p.step ?? 1} of ${p.step_count ?? '?'}</span>`;
    return html`<div class="tours-row">
      <div class="tours-row-head"><strong>${t.title}</strong>${pill}</div>
      <p>${t.description} · ${t.minutes} min</p>
      <div class="tours-row-acts">
        <!-- Always from the first step: after a reload the ship, signal and
             saved chart of an earlier run are gone, so a later step cannot work. -->
        <button class="btn btn-primary" @click=${() => startTour(t.id)}>${p ? 'Start again' : 'Start'}</button>
      </div>
    </div>`;
  }

  override render(): TemplateResult | typeof nothing {
    if (!toursPanelOpen.value) return nothing;
    const tours = availableTours();
    return html`<aside class="tours-panel" aria-label="Guided tours">
      <div class="tours-panel-head">
        <h2>Guided tours</h2>
        <button class="tours-panel-close" aria-label="Close" @click=${() => toursPanelOpen.set(false)}>✕</button>
      </div>
      <p class="tours-panel-lede">Each tour lights up the real controls. You click them yourself, and you can stop at any time.</p>
      ${tours.length
        ? tours.map((t) => this.renderTour(t))
        : html`<p class="tours-panel-lede">There is no tour for the pages you can open yet.</p>`}
    </aside>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tours-panel': ToursPanel;
  }
}
